import Slider from "react-slick"
import { useEffect, useRef, useState, useMemo } from "react"
import { SiteVideoData } from "../../../../interfaces"
import { SiteVideoInline } from "../../../../siteWidgets/videoPlayer/genericInlineVideoPlayer/siteVideoInline"
import { videoSourceOption } from "../../../topStory/windows/contentWindow/videoLiveSourceSelector"
require("./ynet_site_liveVideoPlayerComponent.less")

type FloatingPlayerViewState = "closed" | "minimized" | "expanded"

interface PersistedFloatingPlayerState {
    state: FloatingPlayerViewState
    expiresAt: number
}
declare let window: Window & {
    FLOATING_PLAYER_STATE_TTL_MS: any
}


const FLOATING_PLAYER_STATE_STORAGE_PREFIX = "liveFloatingPlayerState"
const FLOATING_PLAYER_STATE_TTL_MS =  typeof window != "undefined" &&  window.FLOATING_PLAYER_STATE_TTL_MS || 24 * 60 * 60 * 1000;

const getFloatingPlayerStorageKey = (location: any, videoType?: any) => {
    const normalizedLocationValue = typeof location === "string" ? location : ""
    const normalizedTypeValue = typeof videoType === "string" ? videoType : ""
    const normalizedLocation = normalizedLocationValue && normalizedLocationValue.length ? normalizedLocationValue : "default"
    const normalizedType = normalizedTypeValue && normalizedTypeValue.length ? normalizedTypeValue : "default"
    return `${FLOATING_PLAYER_STATE_STORAGE_PREFIX}:${normalizedLocation}:${normalizedType}`
}

const getDefaultFloatingPlayerState = (isMinimizedDefaultView?: boolean): FloatingPlayerViewState => {
    return isMinimizedDefaultView ? "minimized" : "expanded"
}

const getInitialFloatingPlayerState = (storageKey: any, defaultState: FloatingPlayerViewState): FloatingPlayerViewState => {
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
        return defaultState
    }

    try {
        const normalizedStorageKey = String(storageKey)
        const storedRawState = window.localStorage.getItem(normalizedStorageKey)
        if (!storedRawState) {
            return defaultState
        }

        const parsedState = JSON.parse(storedRawState) as PersistedFloatingPlayerState
        const isStateExpired = !parsedState || !parsedState.expiresAt || parsedState.expiresAt < Date.now()
        const isStateValid = parsedState && (parsedState.state === "closed" || parsedState.state === "minimized" || parsedState.state === "expanded")

        if (!isStateValid || isStateExpired) {
            window.localStorage.removeItem(normalizedStorageKey)
            return defaultState
        }

        return parsedState.state
    } catch (e) {
        return defaultState
    }
}

const saveFloatingPlayerState = (storageKey: any, state: FloatingPlayerViewState) => {
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
        return
    }

    try {
        const persistedState: PersistedFloatingPlayerState = {
            state,
            expiresAt: Date.now() + FLOATING_PLAYER_STATE_TTL_MS
        }
        window.localStorage.setItem(String(storageKey), JSON.stringify(persistedState))
    } catch (e) {
    }
}

const getFloatingPlayerViewState = (isOpen: boolean, isMinimizedHidden: boolean): FloatingPlayerViewState => {
    if (isOpen) {
        return "expanded"
    }

    return isMinimizedHidden ? "closed" : "minimized"
}

const getPersistedFloatingPlayerState = (state: FloatingPlayerViewState): FloatingPlayerViewState => {
    return state === "closed" ? "minimized" : state
}

interface LiveVideoPlayerProps {
    videoTitle: string
    videoAutoPlay: boolean
    videoMuteSound: boolean
    liveVideoSource: videoSourceOption
    videoShowSoundBtn: boolean
    liveIndicationText?: string
    isMinimizedVisible: boolean
    videoShowControlPanel: boolean
    isMobileWeb?: boolean
    playerRefForExternal?: any
    externalIsOpenTrigger?: boolean
    isMinimizedDefaultView?: boolean
    onExternalIsOpenTriggerChange?: (isOpen: boolean) => void
    isFloating?: boolean,
    thisLocation: string
    isLogoExist?: boolean
    videoType?: string
    hideVideoAds?: boolean
    poster?: string
    thumbnailTitle?: string
    isFlashingDoteView?: boolean
    onPlayingChange?: (isPlaying: boolean) => void
}

export const LiveVideoPlayer = (props: LiveVideoPlayerProps) => {

    const { isLogoExist, videoType, thisLocation, isMinimizedDefaultView, videoTitle, liveVideoSource, videoAutoPlay, videoMuteSound, videoShowSoundBtn, videoShowControlPanel, isMobileWeb, isMinimizedVisible, externalIsOpenTrigger, playerRefForExternal, liveIndicationText, onExternalIsOpenTriggerChange, hideVideoAds, thumbnailTitle, isFlashingDoteView, onPlayingChange } = props
    const shouldPersistFloatingPlayerState = videoType !== "bepo" && !isMobileWeb
    const floatingPlayerStorageKey = getFloatingPlayerStorageKey(thisLocation, videoType)
    const defaultFloatingPlayerState = getDefaultFloatingPlayerState(isMinimizedDefaultView)
    const initialFloatingPlayerState = isMinimizedVisible && shouldPersistFloatingPlayerState ? getInitialFloatingPlayerState(floatingPlayerStorageKey, defaultFloatingPlayerState) : "expanded"

    const [isPlayerOpen, setIsPlayerOpen] = useState(initialFloatingPlayerState === "expanded")
    const [isMuted, setIsMuted] = useState(videoMuteSound)
    const [isPlaying, setIsPlaying] = useState(videoAutoPlay)
    const [programTitleWidth, setProgramTitleWidth] = useState("0")
    const [isTitleOverFlowing, setIsTitleOverFlowing] = useState(null)
    const [isLiveIndicationHidden, setIsLiveIndicationHidden] = useState(initialFloatingPlayerState === "closed")
    const [isAlreadyOpenedOneTime, setIsAlreadyOpenedOneTime] = useState(false)
    const [mobileBottomHeight, setMobileBottomHeight] = useState(0)
    const [isHydrated, setIsHydrated] = useState(false)
    const [isAutoMinimizedOnScroll, setIsAutoMinimizedOnScroll] = useState(false)
    const [mobileHiddenExpiresAt, setMobileHiddenExpiresAt] = useState<number | null>(null)
    const [isMobilePlayerMuted, setIsMobilePlayerMuted] = useState(true)
    const playerContainerRef: any = useRef<any>(null)
    const mobileTopAnchorRef: any = useRef<any>(null)
    const mobileInlineScrollThresholdRef: any = useRef<number>(0)
    const isPlayerOpenTrigger = externalIsOpenTrigger !== undefined ? externalIsOpenTrigger : isPlayerOpen;
    const shouldHideUntilHydration = isMinimizedVisible && shouldPersistFloatingPlayerState && !isHydrated
    const isMobileMode = !!isMobileWeb && videoType !== "bepo"
    const isMobileInlineMode = isMobileMode && !isMinimizedDefaultView
    const isMobileMinimizedDefaultMode = isMobileMode && !!isMinimizedDefaultView
    const shouldRenderMobileMinimized = isMobileMinimizedDefaultMode || isAutoMinimizedOnScroll
    const isMobileHidden = mobileHiddenExpiresAt !== null && mobileHiddenExpiresAt > Date.now()
    const playerRef: any = useRef<any>(null)
    const titleRef: any = useRef<HTMLElement>(null)
    const settings = {
        dots: false,
        infinite: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        speed: props.videoTitle.length * 200,
        autoplaySpeed: 0,
        rtl: true,
        arrows: false,
        cssEase: "linear"
    }

    const flowPlayerData: SiteVideoData = useMemo(() => {
        return {
            mediaType: "MEDIA_VIDEO",
            url: liveVideoSource ? liveVideoSource.lowResSrc : "",
            fullScreenUrl: liveVideoSource ? liveVideoSource.src : "",
            mediaId: "",
            youtube_id: "",
            isLive: !videoType,
            isMuteAvailable: true,
            blockAbroadViews: false,
            isSoundButtonVisible: videoShowSoundBtn,
            isPlayerBarVisible: videoShowControlPanel,
            isAutoPlaying: isMobileWeb ? true : videoAutoPlay,
            isFloating: true,
            thisLocation: thisLocation,
            poster: videoType === "bepo" && props.poster ? props.poster : "",
            
        }
    }, [liveVideoSource, videoAutoPlay, videoShowControlPanel, videoShowSoundBtn, videoMuteSound])

    const getTitleOverflowStatus = () => {
        const titleElement = titleRef.current as HTMLElement
        const wrapperElement = document.querySelector(".live-video-player  .video-title-wrapper") ? document.querySelector(".live-video-player  .video-title-wrapper") as HTMLElement : document.querySelector(".standart-video-player  .video-title-wrapper") as HTMLElement
        let titleOverflow = false
        if (titleElement) {
            titleOverflow = titleElement.offsetWidth > wrapperElement.offsetWidth
        }
        return titleOverflow
    }

    const updateProgramTitleWidth = () => {
        const titleElement = titleRef.current as HTMLElement

        if (titleElement) {
            setProgramTitleWidth(titleElement.offsetWidth + "px")
        }
    }

    useEffect(() => {
        setIsTitleOverFlowing(getTitleOverflowStatus())
        updateProgramTitleWidth()
    }, [videoTitle])

    useEffect(() => {

        return () => {
            if (playerRef.current) {
                playerRef.current.removeEventListener("play", onPLayEvent)
                playerRef.current.removeEventListener("pause", onPauseEvent)
                playerRef.current.removeEventListener("volumechange", handleVolumeChangeEvent)
            }
        }
    }, [])

    useEffect(() => {
        setIsHydrated(true)
    }, [])

    useEffect(() => {
        if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
            return
        }

        try {
            if (!isMobileMinimizedDefaultMode) {
                setMobileHiddenExpiresAt(null)
                return
            }

            const stored = window.localStorage.getItem(floatingPlayerStorageKey)
            if (!stored) {
                setMobileHiddenExpiresAt(null)
                return
            }

            const parsedState = JSON.parse(stored) as PersistedFloatingPlayerState
            const isStateExpired = !parsedState || !parsedState.expiresAt || parsedState.expiresAt < Date.now()
            const isClosedState = parsedState && parsedState.state === "closed"

            if (isStateExpired) {
                setMobileHiddenExpiresAt(null)
                window.localStorage.removeItem(floatingPlayerStorageKey)
                return
            }

            setMobileHiddenExpiresAt(isClosedState ? parsedState.expiresAt : null)
        } catch (e) {}
    }, [isMobileMinimizedDefaultMode, floatingPlayerStorageKey])

    useEffect(() => {
        if (!isMobileInlineMode || isMobileHidden || isAutoMinimizedOnScroll) {
            return
        }

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting && !isMobilePlayerMuted) {
                        mobileInlineScrollThresholdRef.current = typeof window !== "undefined" ? window.scrollY : 0
                        setIsAutoMinimizedOnScroll(true)
                    }
                })
            },
            { threshold: 0.1 }
        )

        if (mobileTopAnchorRef.current) {
            observer.observe(mobileTopAnchorRef.current)
        }

        return () => observer.disconnect()
    }, [isMobileInlineMode, isMobileHidden, isMobilePlayerMuted, isAutoMinimizedOnScroll])

    useEffect(() => {
        if (!isMobileInlineMode || !isAutoMinimizedOnScroll) {
            return
        }

        const handleScrollBackToInline = () => {
            if (window.scrollY <= mobileInlineScrollThresholdRef.current) {
                setIsAutoMinimizedOnScroll(false)
            }
        }

        window.addEventListener("scroll", handleScrollBackToInline, { passive: true })

        return () => {
            window.removeEventListener("scroll", handleScrollBackToInline)
        }
    }, [isMobileInlineMode, isAutoMinimizedOnScroll])

    useEffect(() => {
        const mobileBanner = document.getElementById("ads.banner");
        if (!mobileBanner) return;

        setMobileBottomHeight(mobileBanner.clientHeight);

        const resizeObserver = new ResizeObserver(() => {
            setMobileBottomHeight(mobileBanner.clientHeight);
        });
        resizeObserver.observe(mobileBanner);

        return () => {
            resizeObserver.disconnect();
        }
    }, [])

    useEffect(() => {
        if (!isMinimizedVisible || !shouldPersistFloatingPlayerState || externalIsOpenTrigger !== undefined) {
            return
        }

        const viewState = getFloatingPlayerViewState(isPlayerOpen, isLiveIndicationHidden)
        saveFloatingPlayerState(floatingPlayerStorageKey, getPersistedFloatingPlayerState(viewState))
    }, [isPlayerOpen, isLiveIndicationHidden, isMinimizedVisible, shouldPersistFloatingPlayerState, externalIsOpenTrigger, floatingPlayerStorageKey])

    const onPLayEvent = () => {
        setIsPlaying(true)
        if (onPlayingChange) onPlayingChange(true)
    }

    const onPauseEvent = () => {
        setIsPlaying(false)
        if (onPlayingChange) onPlayingChange(false)
    }

    const handleVolumeChangeEvent = () => {
        if (playerRef.current.muted) {
            setIsMuted(true)
            if (isMobileMode) {
                setIsMobilePlayerMuted(true)
            }
        } else {
            setIsMuted(false)
            if (isMobileMode) {
                setIsMobilePlayerMuted(false)
            }
        }
    }

    const handleOpenPlayer = () => {
        if (externalIsOpenTrigger !== undefined && onExternalIsOpenTriggerChange) {
            onExternalIsOpenTriggerChange(true)
        } else {
            setIsPlayerOpen(true)
        }

        if (isAlreadyOpenedOneTime) {
            if (playerRef.current) {
                playerRef.current.play();

                if (playerRef.current.muted) {
                    playerRef.current.toggleMute();
                }
            }
        } else {
            setIsAlreadyOpenedOneTime(true)
        }
    }

    const handleClosePlayer = () => {
        if (externalIsOpenTrigger !== undefined && onExternalIsOpenTriggerChange) {
            onExternalIsOpenTriggerChange(false)
        } else {
            setIsPlayerOpen(false)
        }

        if (isPlaying) {
            playerRef.current.pause();
        }
    }

    const handleCloseLiveIndication = (e) => {
        e.stopPropagation();
        setIsLiveIndicationHidden(true);
    }

    const hideMobilePlayerFor24h = () => {
        if (!isMobileMinimizedDefaultMode) {
            return
        }

        saveFloatingPlayerState(floatingPlayerStorageKey, "closed")
        setMobileHiddenExpiresAt(Date.now() + FLOATING_PLAYER_STATE_TTL_MS)
    }

    const handleCloseMobileMinimized = (e) => {
        e.stopPropagation()

        if (isMobileMinimizedDefaultMode) {
            hideMobilePlayerFor24h()
            return
        }

        if (playerRef.current && !playerRef.current.muted) {
            playerRef.current.toggleMute()
        }
        setIsMobilePlayerMuted(true)
        setIsAutoMinimizedOnScroll(false)
    }

    const handlePlayerLoaded = (player) => {
        playerRef.current = player;
        if (playerRefForExternal) {
            playerRefForExternal.current = player;
        }
        player.setOpts({ muted: isMobileMode ? isMobilePlayerMuted : videoMuteSound });

        if (playerRef.current) {
            playerRef.current.addEventListener("play", onPLayEvent)
            playerRef.current.addEventListener("pause", onPauseEvent)
            playerRef.current.addEventListener("volumechange", handleVolumeChangeEvent)
        }
    }

    const handlePlayerMuted = () => {
        if (playerRef.current) {
            playerRef.current.toggleMute();
        }
    }

    if (isMobileMode && isMobileHidden) {
        return null
    }

    if (isMobileMode) {
        if (shouldRenderMobileMinimized) {
            return (
                <div
                    className={`${videoType ? "standart-video-player " + videoType : "live-video-player"} with-minimized`}
                    style={shouldHideUntilHydration ? { visibility: "hidden" } : undefined}
                >
                    <div className={`live-player-container open`} style={mobileBottomHeight ? { bottom: `${mobileBottomHeight}px` } : {}}>
                        <SiteVideoInline
                            data={flowPlayerData}
                            isShareButton={false}
                            applyStickyness={false}
                            onPlayerLoaded={handlePlayerLoaded}
                            hideVideoAds={props.hideVideoAds ? props.hideVideoAds : undefined}
                        />

                        <div className="control-panel">
                            <div className={`mute-btn ${isMobilePlayerMuted ? "muted" : "unmuted"}`} onClick={handlePlayerMuted}></div>

                            <div className="title-live-wrapper">
                                {isLogoExist ? <div className={`player-logo ${videoType}`} ></div> : <div className="live-indication">
                                    <div className="live-text">{liveIndicationText}</div>
                                </div>}

                                <div className="video-title-wrapper">
                                    {isTitleOverFlowing ?
                                        <>
                                            <div className="fadder-right"></div>

                                            <div className="slider-wrapper" style={{ width: programTitleWidth }}>
                                                <Slider {...settings}>
                                                    <div className="video-title" ref={ref => titleRef.current = ref} dangerouslySetInnerHTML={{ __html: videoTitle }}></div>

                                                    <div className="video-title" ref={ref => titleRef.current = ref} dangerouslySetInnerHTML={{ __html: videoTitle }}></div>
                                                </Slider>
                                            </div>

                                            <div className="fadder-left"></div>
                                        </>
                                        :
                                        <div className="video-title" ref={ref => titleRef.current = ref} dangerouslySetInnerHTML={{ __html: videoTitle }}></div>
                                    }
                                </div>
                            </div>

                            <div className="close-btn" onClick={handleCloseMobileMinimized}></div>
                        </div>
                    </div>
                </div>
            )
        }

        return (
            <div
                className={`${videoType ? "standart-video-player " + videoType : "live-video-player"} without-minimized`}
                style={shouldHideUntilHydration ? { visibility: "hidden" } : undefined}
            >
                <div ref={mobileTopAnchorRef} style={{ position: "absolute", top: 0, width: 1, height: 1, pointerEvents: "none" }}></div>
                <div className="liveVideoPlayerMutamWrapper" ref={playerContainerRef}>
                    <div className="liveVideoPlayerMutamComponenta">
                        <SiteVideoInline
                            data={flowPlayerData}
                            isShareButton={false}
                            applyStickyness={false}
                            onPlayerLoaded={handlePlayerLoaded}
                            hideVideoAds={props.hideVideoAds ? props.hideVideoAds : undefined}
                        />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            className={`${videoType ? "standart-video-player " + videoType : "live-video-player"} ${isMinimizedVisible ? "with-minimized" : "without-minimized"}`}
            style={shouldHideUntilHydration ? { visibility: "hidden" } : undefined}
        >
            {isMinimizedVisible &&
                <div className={`minimized-live-indication ${isLiveIndicationHidden ? "hidden" : ""}`} onClick={handleOpenPlayer}>
                    <div className="thumbnailTitle">
                        <span>{thumbnailTitle || "LIVE"}</span>
                         {isFlashingDoteView && <img className="live-dot" src="https://www.ynet.co.il/Common/images/Video_live_dot.gif" alt="Live"></img>}                      
                    </div>
                    <div className="close-btn" onClick={handleCloseLiveIndication}></div>
                </div>
            }

            <div className={`live-player-container ${isPlayerOpenTrigger ? "open" : "closed"}`} style={isMobileWeb && mobileBottomHeight ? { bottom: `${mobileBottomHeight}px` } : {}}>
                <SiteVideoInline
                    data={flowPlayerData}
                    isShareButton={false}
                    applyStickyness={false}
                    onPlayerLoaded={handlePlayerLoaded}
                    hideVideoAds={props.hideVideoAds ? props.hideVideoAds : undefined}
                />

                <div className="control-panel">
                    {/* <div className={`play-btn ${isPlaying ? "playing" : "paused"}`} onClick={handlePlayBtnClick}></div> */}

                    <div className={`mute-btn ${isMuted ? "muted" : "unmuted"}`} onClick={handlePlayerMuted}></div>

                    <div className="title-live-wrapper">
                        {isLogoExist ? <div className={`player-logo ${videoType}`} ></div> : <div className="live-indication">
                            {/* <img className="dot-gif" src={"https://www.ynet.co.il/images/video/dot-gif.gif"} alt="Live"></img> */}
                            <div className="live-text">{liveIndicationText}</div>
                        </div>}

                        <div className="video-title-wrapper">
                            {isTitleOverFlowing ?
                                <>
                                    <div className="fadder-right"></div>

                                    <div className="slider-wrapper" style={{ width: programTitleWidth }}>
                                        <Slider {...settings}>
                                            <div className="video-title" ref={ref => titleRef.current = ref} dangerouslySetInnerHTML={{ __html: videoTitle }}></div>

                                            <div className="video-title" ref={ref => titleRef.current = ref} dangerouslySetInnerHTML={{ __html: videoTitle }}></div>
                                        </Slider>
                                    </div>

                                    <div className="fadder-left"></div>
                                </>
                                :
                                <div className="video-title" ref={ref => titleRef.current = ref} dangerouslySetInnerHTML={{ __html: videoTitle }}></div>
                            }
                        </div>
                    </div>

                    <div className="close-btn" onClick={handleClosePlayer}></div>
                </div>
            </div>
        </div>
    )
}

LiveVideoPlayer.siteScriptName = "LiveVideoPlayer"